import React, { useEffect, useRef, useState } from "react";
import {
  CPButton,
  CustomisedBackPath,
  CustomisedTertiaryButton,
  FlexColumn,
  ModalBox,
  TextWrapper,
  Wrapper,
} from "../../constants/common/StyledGlobalConstants";
import { connect, useDispatch, useSelector } from "react-redux";
import * as Actions from "../../store/actions/contract/myContractDetailsActions";
import { Line } from "@vds/lines";
import BackPath from "../../assets/icons/back-path.png";
import styled from "styled-components";
import { ModalBody } from "@vds/modals";
import RequestDetails from "./RequestDetails";
import "./myContract.css";
import ReviewContract from "./reviewContract";
import DiscardModal from "./DiscardModal";
import SubmitRequest from "./submitRequest";
import { Stepper, Step, StepTitle, useStepper } from "react-progress-stepper";
import * as contract from "../../api/contract/index";
import { Loader } from "@vds/loaders";
import { constructCaseHyperLink } from "../../util/helpURLNavigation";
import { pendoTrack } from "../../util/pendoUtil";
import { useTranslation } from "react-i18next";
import { DEVICE_BREAKPOINTS } from "../../constants/appConstants";
import { setHeaderAriaHidden } from "../../store/actions/global/global";
import PropTypes from "prop-types";

const { TABLET, DESKTOP } = DEVICE_BREAKPOINTS;

const MainWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 3rem;
  margin-bottom: 3rem;
  @media (max-width: 480px) {
    margin-top: 1.5rem;
  }
  :focus-visible {
    outline: none;
  }
`;

const SubWrapper = styled.div`
  width: ${(props) => (props.windowSize.width < DESKTOP ? "100%" : "300px")};
`;

const StepperCount = styled.div`
  color: #747474;
  font-family: "neue-haas-grotesk-text";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
`;

const StepperTitle = styled.div`
  color: #000;
  font-family: "neue-haas-grotesk-text";
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px;
  margin-top: 3px;
`;

const MyContractDetails = (props) => {
  const { t } = useTranslation("contractsTranslation");
  const [isOpen, setIsOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(1);
  const { step, incrementStep, decrementStep } = useStepper(0, 2);
  const [loader, setLoader] = useState(false);
  const [isRequestFailed, setIsRequestFailed] = useState(false);
  const stepperRef = useRef();
  const dispatch = useDispatch();
  const { windowSize, contractDetailsType, isDuplicateRequest } = props;

  const { account_id } = useSelector((state) => state.ACCOUNT);

  const isMobile = windowSize.width < DESKTOP;
  const isTablet = windowSize.width < TABLET;

  useEffect(() => {
    if (props.isActiveCancellationSuccess) {
      handleNavigate(t("next"));
    }
  }, [props.isActiveCancellationSuccess]);

  const focusStepper = () => {
    stepperRef?.current?.focus();
  };

  const shouldShowDiscardModal = () => {
    return (props.dateToBegin !== "" || props.contractContact !== "" || props.reason !== "") && step <= 1;
  };

  const resetAndClose = (caseCreationSuccess) => {
    setIsOpen(false);
    setCurrentIndex(1);
    props.toggleViewContractDetails(false);
    props.setCaseCreationSuccess(caseCreationSuccess);
    props.resetContractDetails();
  };

  const handleBack = () => {
    focusStepper();
    if (shouldShowDiscardModal()) {
      setIsOpen(true);
    } else if (step === 2) {
      decrementStep();
      decrementStep();
      dispatch(setHeaderAriaHidden(false));
      resetAndClose(true);
    } else {
      dispatch(setHeaderAriaHidden(false));
      resetAndClose(false);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
  };

  const discardChanges = () => {
    setIsOpen(false);
    props.toggleViewContractDetails(false);
    setIsRequestFailed(false);
    props.resetContractDetails();
    dispatch(setHeaderAriaHidden(false));
    if (step === 1) decrementStep();
    setCurrentIndex(1);
  };

  const handleNavigate = (type) => {
    focusStepper();
    let index = currentIndex;
    type === t("next") ? index++ : index--;
    type === t("next") ? incrementStep() : decrementStep();
    if (type === t("back")) props.resetCancellationCaseDetails();
    setCurrentIndex(index);
  };

  const handleDisableSubmit = () => {
    const {
      dateToBegin,
      contactName,
      contactEmailAddress,
      contactPhoneNumber,
      reason,
      contactNameErr,
      contactPhoneNumberErr,
      contactEmailAddressErr,
      reasonDescription,
    } = props;

    return !(
      dateToBegin &&
      contactName &&
      contactEmailAddress &&
      contactPhoneNumber &&
      reason &&
      !contactEmailAddressErr &&
      !contactNameErr &&
      !contactPhoneNumberErr &&
      (reason !== "Other" || reasonDescription?.length > 0) &&
      (!reasonDescription || reasonDescription.length <= 500)
    )
  };

  const buildRequestBody = () => {
    return {
      origin: "VZC-Customer Portal",
      accountId: account_id,
      productFamily: "Reveal",
      status: "New",
      cancellationBusinessReason: props.reason,
      productType: "Reveal",
      issueType: "Cancellation Request",
      emailId: props.contactEmailAddress,
      name: props.contactName,
      type: props.contractDetailsType === "change" ? "Change" : "Cancellation",
      term: props.dateToBegin,
      description: props.reasonDescription,
      phone: props.contactPhoneNumber,
      region: constructCaseHyperLink(props.regionId)[0],
      language: constructCaseHyperLink(props.regionId)[1],
      brands: props.erpBusinessPartner?.data?.brands || "",
    };
  };

  const handleSubmit = async () => {
    setLoader(true);
    try {
      const reqBody = buildRequestBody();
      const responseArray = await contract.submitMyContractCall(reqBody);
      setLoader(false);

      if (responseArray?.data?.statusCode === "200" && responseArray?.data?.data?.length !== 0) {
        pendoTrack("Customer Portal | Contracts And Services | Edit Services | Submit | Success", {
          status: "Success",
          statusCode: responseArray.data.statusCode,
          accountId: props.accountNumber,
        });
        let index = currentIndex;
        index++;
        incrementStep();
        setCurrentIndex(index);
        setIsRequestFailed(false);
      } else {
        throw new Error("Invalid response");
      }
    } catch (err) {
      console.log("Error in submitting the request:", err);
      pendoTrack("Customer Portal | Contracts And Services | Edit Services | Submit | Failed", {
        status: "Failed",
        accountId: props.accountNumber,
      });
      setIsRequestFailed(true);
      setLoader(false);
    }
  };
  const getMarginTop = (isMobile) => (isMobile ? "10rem" : "5rem");
  const getWidth = (isMobile) => (isMobile ? "100%" : "54%");
  const getWidthButton = (isMobile) => (isMobile ? "100%" : "75%");

  const getModificationType = (contractDetailsType, t) => {
    return contractDetailsType === "change" ? t("Change") : t("Cancellation");
  };
  const getPaddingBottom = (isMobile, regionId) => {
    return isMobile && (regionId === "840" || regionId === "124") ? "5em" : "";
  };
  const getPadding = (isTablet, isMobile) => {
    if (isTablet) return "1rem";
    return isMobile ? "12px 4rem 0 0" : "12px 0 0 0";
  };
  const getButtonWidth = (isTablet) => (isTablet ? "100%" : "autoTight");
  const getWarrperWidth = (isTablet) => (isTablet ? "100%" : "");
  const getWarrperWidthTablet = (isTablet) => (isTablet ? "45%" : "autoTight");

  return (
    <>
      <ModalBox
        id="my-contract-details-modal"
        opened={props.viewContractDetailsToggle}
        ariaLabel="Contract Details Modal"
        fullScreenDialog={true}
        disableOutsideClick={true}
        mobHeight={`${props.windowSize.height}px !important`}
        mobMaxHeight={`${props.windowSize.height}px !important`}
        mobWidth={`${props.windowSize.width}px !important`}
        mobMaxWidth={`${props.windowSize.width}px !important`}
        closeButton={
          <>
            <Wrapper display="flex" margin="1.5rem 0">
              <CustomisedBackPath
                tabIndex={0}
                aria-label={t("Back to my contract")}
                src={BackPath}
                onClick={() => handleBack()}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleBack()}
              />
              <TextWrapper
                aria-label="Contracts and subscriptions"
                aria-level="1"
                fontFamily="neue-haas-grotesk-display"
                fontSize="1.5rem"
                fontWeight="700"
                lineHeight="2rem"
              >
                {t("Contracts and subscriptions")}
              </TextWrapper>
            </Wrapper>
            {<Line type="xLight" inverted={false} />}
          </>
        }
      >
        <ModalBody
          style={{
            paddingTop: "0rem",
            marginBottom: getMarginTop(isMobile),
          }}
        >
          {step <= 1 && (
            <MainWrapper ref={stepperRef}>
              <SubWrapper windowSize={props.windowSize}>
                {isMobile && <StepperCount>Step {step + 1} of 2</StepperCount>}
                {step === 0 && isMobile && <StepperTitle aria-label="Request details">{t("Request details")}</StepperTitle>}
                {step === 1 && isMobile && <StepperTitle aria-label="Review and submit">{t("Review and submit")}</StepperTitle>}
                <Stepper step={step} animation={false}>
                  <Step>
                    {props.windowSize.width >= DESKTOP && (
                      <StepTitle>
                        <span className="sr-only" tabIndex="-1">
                          {(() => {
                            let stepText;
                            if (step === 0) {
                              stepText = "Step 1, Request details. Current step";
                            } else if (step > 0) {
                              stepText = "Request details step completed";
                            } else {
                              stepText = "Step 1, Request details";
                            }
                            return stepText;
                          })()}
                        </span>
                        <span aria-hidden="true" tabIndex="-1">
                          {t("Request details")}
                        </span>
                      </StepTitle>
                    )}
                  </Step>
                  <Step>
                    {props.windowSize.width >= DESKTOP && (
                      <StepTitle>
                        {(() => {
                          let stepText;
                          if (step === 1) {
                            stepText = "Step 2, Review and submit. Current step";
                          } else if (step > 1) {
                            stepText = "Review and submit step completed";
                          } else {
                            stepText = "Step 2, Review and submit";
                          }
                          return (
                            <span className="sr-only" tabIndex="-1">
                              {stepText}
                            </span>
                          );
                        })()}
                        <span aria-hidden="true" tabIndex="-1">
                          {t("Review and submit")}
                        </span>
                      </StepTitle>
                    )}
                  </Step>
                </Stepper>
              </SubWrapper>
            </MainWrapper>
          )}

          <Wrapper display="flex" justifyContent="center">
            <Loader active={loader || props.isLoader} fullscreen={true} />
            <FlexColumn width={getWidth(isMobile)}>
              {currentIndex === 1 && <RequestDetails />}
              {currentIndex === 2 && (
                <ReviewContract
                  {...props}
                  contractDetailsType={contractDetailsType}
                  pageTitle={t("Review request")}
                  pageBodyText={t("Review and update your request before submitting. For more information, see")}
                  notification={isRequestFailed}
                  spinner={false}
                  reviewContractData={[
                    {
                      "Modification type": getModificationType(contractDetailsType, t),
                      "Date to begin": t(`${props.dateToBegin}`),
                      Name: `${props.contactName}`,
                      Reason: t(`${props.reason}`),
                    },
                  ]}
                />
              )}
              {currentIndex === 3 && (
                <SubmitRequest
                  pageTitle={t("Request submitted")}
                  pageBodyText={t(
                    "We will contact you within 2-5 business days to discuss next steps. Confirmation of this request will be emailed to the contact you provided."
                  )}
                  pageBodyTextOne={t("You can track the progress of this request in your Support cases.")}
                  pageBodyTextTwo={t("For more information, see ")}
                ></SubmitRequest>
              )}
            </FlexColumn>
          </Wrapper>
        </ModalBody>

        <footer style={{ paddingBottom: getPaddingBottom(isMobile, props.regionId) }} className="fixed-bottom-footer">
          <Line type="xLight" inverted={false} />
          <Wrapper
            display="flex"
            justifyContent={isTablet ? "" : "flex-end"}
            padding={getPadding(isTablet, isMobile)}
            width={getWidthButton(isMobile)}
          >
            {currentIndex === 1 && (
              <CPButton
                buttonWidth={getButtonWidth(isTablet)}
                buttonHeight={"40px"}
                id="my-contract-next-button"
                boxButton={true}
                disabled={handleDisableSubmit()}
                onClick={props.getActiveCancellationCaseList}
                ariaLabel="Next"
                marginLeft="8px"
                fontPaddingLeft="0.75rem"
                fontPaddingRight="0.75rem"
              >
                {t("NEXT")}
              </CPButton>
            )}
            {currentIndex === 2 && (
              <Wrapper gap="1rem" display="flex" width={getWarrperWidth(isTablet)} flexDirection="row">
                <CustomisedTertiaryButton
                  ariaLabel="Back"
                  onClick={() => handleNavigate("back")}
                  width={getWarrperWidthTablet(isTablet)}
                  id="my-contract-back-button"
                >
                  {t("BACK")}
                </CustomisedTertiaryButton>
                {isDuplicateRequest ? (
                  <CPButton
                    buttonWidth={getWarrperWidthTablet(isTablet)}
                    buttonHeight={"40px"}
                    boxButton={true}
                    onClick={() => {
                      discardChanges();
                    }}
                    ariaLabel="OKAY"
                    fontPaddingLeft="0.75rem"
                    fontPaddingRight="0.75rem"
                    id="my-contract-okay-button"
                  >
                    {t("OKAY")}
                  </CPButton>
                ) : (
                  <CPButton
                    buttonWidth={getWarrperWidthTablet(isTablet)}
                    buttonHeight={"40px"}
                    boxButton={true}
                    onClick={() => {
                      handleSubmit();
                    }}
                    ariaLabel="Submit"
                    fontPaddingLeft="0.75rem"
                    fontPaddingRight="0.75rem"
                    id="my-contract-submit-button"
                  >
                    {t("SUBMIT")}
                  </CPButton>
                )}
              </Wrapper>
            )}
            {currentIndex === 3 && (
              <CPButton
                width={getButtonWidth(isTablet)}
                buttonHeight={"40px"}
                boxButton={true}
                onClick={() => {
                  handleBack();
                }}
                ariaLabel="Close"
                marginLeft="8px"
                fontPaddingLeft="0.75rem"
                fontPaddingRight="0.75rem"
                id="my-contract-close-button"
              >
                {t("CLOSE")}
              </CPButton>
            )}
          </Wrapper>
        </footer>
      </ModalBox>
      <DiscardModal
        open={isOpen}
        onClose={() => closeModal()}
        yesButtonId="my-contract-lose-change-yes-button"
        noButtonId="my-contract-lose-change-no-button"
        onYesButtonClick={() => discardChanges()}
        windowSize={props.windowSize}
      />
    </>
  );
};
const mapDispatchToProps = {
  toggleViewContractDetails: Actions.toggleViewContractDetails,
  setContractDetailsType: Actions.setContractDetailsType,
  resetContractDetails: Actions.resetContractDetails,
  setCaseCreationSuccess: Actions.setCaseCreationSuccess,
  getActiveCancellationCaseList: Actions.getActiveCancellationCasesList,
  resetCancellationCaseDetails: Actions.resetCancellationCaseDetails,
};
const mapStateToProps = (state) => {
  return {
    viewContractDetailsToggle: state.CONTRACT.viewContractDetailsToggle,
    contractDetailsType: state.CONTRACT.contractDetailsType,
    windowSize: state.ACCOUNT.windowSize,
    dateToBegin: state.CONTRACT.dateToBegin,
    contractContact: state.CONTRACT.contractContact,
    reason: state.CONTRACT.reason,
    reasonDescription: state.CONTRACT.reasonDescription,
    contactNameErr: state.CONTRACT.contactNameErr,
    contactEmailAddressErr: state.CONTRACT.contactEmailAddressErr,
    contactPhoneNumberErr: state.CONTRACT.contactPhoneNumberErr,
    contactName: state.CONTRACT.contactName,
    contactEmailAddress: state.CONTRACT.contactEmailAddress,
    contactPhoneNumber: state.CONTRACT.contactPhoneNumber,
    regionId: state.ACCOUNT.accountUserTokenDetails.accountRegionId,
    accountNumber: state.ACCOUNT.accountNumber,
    isActiveCancellationSuccess: state.CONTRACT.isActiveCancellationSuccess,
    isLoader: state.CONTRACT.isLoader,
    isDuplicateRequest: state.CONTRACT.isDuplicateRequest,
    userLanguage: state.ACCOUNT.userLanguage,
    erpBusinessPartner: state.ACCOUNT.erpBusinessPartner,
  };
};

MyContractDetails.propTypes = {
  windowSize: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
  }).isRequired,
  contractDetailsType: PropTypes.string,
  isDuplicateRequest: PropTypes.bool,
  dateToBegin: PropTypes.string,
  contractContact: PropTypes.string,
  reason: PropTypes.string,
  reasonDescription: PropTypes.string,
  contactName: PropTypes.string,
  contactEmailAddress: PropTypes.string,
  contactPhoneNumber: PropTypes.string,
  contactNameErr: PropTypes.any,
  contactEmailAddressErr: PropTypes.any,
  contactPhoneNumberErr: PropTypes.any,
  setCaseCreationSuccess: PropTypes.func,
  resetCancellationCaseDetails: PropTypes.func,
  resetContractDetails: PropTypes.func,
  toggleViewContractDetails: PropTypes.func,
  viewContractDetailsToggle: PropTypes.bool,
  regionId: PropTypes.string,
  erpBusinessPartner: PropTypes.any,
  isActiveCancellationSuccess: PropTypes.bool,
  accountNumber: PropTypes.string,
  isLoader: PropTypes.bool,
  getActiveCancellationCaseList: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(MyContractDetails);
